import { createError } from '@adv-frontend/error';
import { MaybePromise } from '@reduxjs/toolkit/dist/query/tsHelpers';
import { DEFAULT_VERSION, PersistState } from 'redux-persist';
import { JSONCompatible } from '@direct-frontend/stdlib';
import { PersistMigrate } from './buildCreatePersistentSlice';
import { TypeGuard } from '../rtk-types';

// Сколько может хранится бэкап до удаления (в мс)
export const BACKUP_TTL_MS = 1000 * 60 * 60 * 24 * 7; // 1 неделя

/**
 * Тип объекта, содержащего данные state и сторонние данные для persist
 * @typeParam State - тип данных, хранящихся в persist
 */
export type PersistedState<State> = PersistentData<State> & PersistedStateMeta;

/**
 * Дополнительная информация к стейту
 */
type PersistedStateMeta = { _persist: PersistState; _backups?: Record<number, Backup<unknown>> };

/**
 * Информация о бэкапе версии
 */
type Backup<State> = {
    persistentData: State;
    createdAt: number;
};

/**
 * Обертка, указывающая, где хранится persist данные
 */
export type PersistentData<State> = { persistentData: State };

/**
 * Ошибка процесса миграции
 */
const PersistMigrationError = createError<{ state: JSONCompatible<PersistedStateMeta>; sliceName: string }>(
    'PersistMigrationError'
);

/**
 * Извлекает state из MigrationBuilder
 */
export type InferStateFromMigrations<T> = T extends MigrationBuilder<infer State> ? State : never;

/**
 * Метод миграции с одной версии state в другую
 * @typeParam FromState - state, с которого проводим миграцию
 * @typeParam ToState - state, на который проводим миграцию
 */
export type Migration<FromState, ToState> = (state: FromState) => MaybePromise<ToState>;

/**
 * Сборщик миграций redux-persist
 * @typeParam State - итоговый тип state, который будет после всех миграций
 */
export class PersistMigrationBuilder<State> implements EmptyMigrationBuilder<State> {
    public initialMigration?: Migration<void, PersistentData<unknown>>;
    private migrations: Migration<PersistentData<unknown>, PersistentData<unknown>>[] = [];

    /**
     * Добавляет миграцию, которая вызывается при отсутствии стейта
     * @param migrate миграция
     * @returns сборщик миграций
     * @typeParam State - тип state, который будет после проведения этой миграции
     */
    public addInitialMigration(migrate: Migration<void, PersistentData<State> | undefined>) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
        this.initialMigration = migrate as unknown as Migration<void, PersistentData<unknown>>;
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
        return this as MigrationBuilder<State>;
    }

    /**
     * Добавляет миграцию в цепь
     * @param migrate миграция
     * @returns сборщик миграций
     * @typeParam ToState - новый тип state, который будет после проведения этой миграции
     */
    public addMigration<ToState>(migrate: Migration<PersistentData<State>, PersistentData<ToState>>) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
        this.migrations.push(migrate as unknown as Migration<PersistentData<unknown>, PersistentData<unknown>>);
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
        return this as unknown as MigrationBuilder<ToState>;
    }

    /**
     * @returns последняя версия миграций
     */
    public getVersion = () => this.migrations.length + 1;

    /**
     * Объединяет все миграцию в одну, для проведения миграции в redux-persist
     * @returns все миграции, объединенные в одну
     */
    public mergeMigrations(validateState: TypeGuard<State>, sliceName: string) {
        return async (
            state: PersistedState<unknown> | undefined,
            currentVersion: number
        ): Promise<PersistentData<State> | undefined> => {
            if (state) {
                state._backups = this.cleanupBackups(state._backups ?? {});
            }

            let inboundVersion = state?._persist?.version ?? DEFAULT_VERSION;

            // Версия не изменилась – миграцию применять не нужно, но проверяем корректность данных
            if (inboundVersion === currentVersion) {
                if (!validateState(state?.persistentData)) {
                    console.error(
                        new PersistMigrationError('Failed state validation', {
                            cause: undefined,
                            additional: {
                                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
                                state: state as JSONCompatible<PersistedState<unknown>>,
                                sliceName,
                            },
                            groupBy: {
                                sliceName,
                            },
                        })
                    );
                    return undefined;
                }

                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
                return state as PersistedState<State>;
            }

            let migratedState: PersistedState<unknown> | undefined = state;

            // Версия из storage больше, чем версия в конфиге
            if (inboundVersion > currentVersion) {
                migratedState = this.revertMigrations(state, currentVersion);
                inboundVersion = migratedState?._persist.version ?? DEFAULT_VERSION;
            }

            // Стейта или бэкапа нет
            if (!migratedState) {
                const initedState = await this.initialMigration?.();
                if (!initedState) {
                    return;
                }

                migratedState = {
                    persistentData: initedState.persistentData,
                    _persist: {
                        version: 1,
                        rehydrated: false,
                    },
                    _backups: {},
                };
                inboundVersion = 1;
            }

            migratedState._backups = migratedState._backups ?? {};
            migratedState._backups[inboundVersion] = {
                persistentData: migratedState.persistentData,
                createdAt: Date.now(),
            };
            try {
                // Версия != индекс
                const lastVersion = Math.min(currentVersion, this.getVersion());
                for (let i = inboundVersion - 1; i < lastVersion - 1; i++) {
                    // Нет возможности добавить в массив миграций значение 'undefined'
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- см. выше
                    const state = await this.migrations[i]!(migratedState);
                    migratedState.persistentData = state.persistentData;
                }
            } catch (error) {
                console.error(
                    new PersistMigrationError('Error during migration', {
                        cause: error,
                        additional: {
                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
                            state: migratedState as JSONCompatible<PersistedState<unknown>>,
                            sliceName,
                        },
                        groupBy: {
                            sliceName,
                        },
                    })
                );

                // Если миграция не возвращает state, то используется initialState
                return;
            }

            if (!validateState(migratedState.persistentData)) {
                console.error(
                    new PersistMigrationError('Failed state validation after migration', {
                        cause: undefined,
                        additional: {
                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
                            state: migratedState as JSONCompatible<PersistedState<unknown>>,
                            sliceName,
                        },
                        groupBy: { sliceName },
                    })
                );
                return undefined;
            }

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
            return migratedState as unknown as PersistedState<State>;
        };
    }

    private revertMigrations(
        state: PersistedState<unknown> | undefined,
        currentVersion: number
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- автоматический игнор в рамках DIRECT-265451. Исправьте при следующем редактировании при помощи type-assert, чтобы избежать возможных ложно-положительных проверок typecheck!
        // @ts-expect-error DIRECT-235591 TS7030
    ): PersistedState<unknown> | undefined {
        const backups = state?._backups;
        if (!backups) {
            return;
        }

        for (let i = currentVersion; i > 0; i--) {
            const backup = backups[i];
            if (!backup) {
                continue;
            }

            return {
                persistentData: backup.persistentData,
                _persist: {
                    version: i,
                    rehydrated: false,
                },
                _backups: backups,
            };
        }
    }

    private cleanupBackups(backups: Record<number, Backup<unknown>>): Record<number, Backup<unknown>> {
        const newBackup: Record<number, Backup<unknown>> = {};
        const versions = Object.keys(backups)
            .map(key => parseInt(key))
            .filter(key => !isNaN(key));
        for (const version of versions) {
            const backup = backups[version];
            if (!backup || backup.createdAt + BACKUP_TTL_MS < Date.now()) {
                continue;
            }

            newBackup[version] = backup;
        }

        return newBackup;
    }

    /**
     * Возвращает метод миграции с переданной версии на следующую
     * @internal используется для тестов
     * @param fromVersion версия, от которой миграция
     * @returns метод миграции
     */
    public getVersionMigration(
        fromVersion: number
    ): Migration<PersistentData<unknown>, PersistentData<unknown>> | undefined {
        return this.migrations[fromVersion - 1];
    }
}

/**
 * Сборщик миграций redux-persist, который еще не имеет миграций и позволяет добавить init миграцию
 * @typeParam State - тип первой версии state
 */
export interface EmptyMigrationBuilder<State> extends MigrationBuilder<State> {
    /**
     * Добавляет миграцию, которая вызывается при отсутствии стейта
     * @param migrate миграция
     * @returns сборщик миграций
     * @typeParam State - тип state, который будет после проведения этой миграции
     */
    addInitialMigration(migrate: Migration<void, PersistentData<State> | undefined>): MigrationBuilder<State>;
}

/**
 * Сборщик миграций redux-persist
 * @typeParam State - тип последнего имеющегося в цепочки миграций state
 */
export interface MigrationBuilder<State> {
    /**
     * Добавляет миграцию в цепь
     * @param migrate миграция
     * @returns сборщик миграций
     * @typeParam ToState - новый тип state, который будет после проведения этой миграции
     */
    addMigration<ToState>(
        migrate: Migration<PersistentData<State>, PersistentData<ToState>>
    ): MigrationBuilder<ToState>;
    /**
     * @returns последняя версия миграций
     */
    getVersion(): number;
    /**
     * Объединяет все миграцию в одну, для проведения миграции в redux-persist
     * @returns все миграции, объединенные в одну
     */
    mergeMigrations(
        validateState: TypeGuard<State>,
        sliceName: string
    ): PersistMigrate<PersistedState<unknown> | undefined, PersistentData<State>>;
}

/**
 * Метод создания нового сборщика миграций
 * @returns сборщик миграций
 * @typeParam State - тип первой версии стейта persistent-слайса
 */
export function createPersistMigrate<State>(): EmptyMigrationBuilder<State> {
    return new PersistMigrationBuilder<State>();
}
